// @ts-ignore
import { Component, AfterViewInit, OnInit } from '@angular/core';
import { Area } from '../../shared/models';
// @ts-ignore
import { ActivatedRoute } from '@angular/router';
import { AreaService } from '../../shared/services';
import { AreaType } from '../../shared/constants/index';
import { Geometry } from '../../shared/models/geometry/index';

declare var google

@Component({
    selector: 'map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss']
})
export class MapComponent {
    areas: Area[] = this.route.snapshot.data['areas'] || []
    map
    drawingManager
    infoWindow
    searchBox
    searchResultMarkers = []
    mapBounds

    private readonly areaLabelId: string = `areaLabel_${new Date().valueOf()}`
    private readonly yellowPinIconUrl = 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png'

    private readonly _shapeOptions = {
        fillColor: '#9ea2a8',
        fillOpacity: 0.5,
        strokeOpacity: 0.5,
        strokeWeight: 1,
        clickable: true,
        editable: false,
        draggable: false,
        zIndex: 1
    }

    private readonly _nonExclusiveShapeOptions = {
      fillColor: '#14d333',
      fillOpacity: 0.5,
      strokeOpacity: 0.5,
      strokeWeight: 1,
      clickable: true,
      editable: false,
      draggable: false,
      zIndex: 1
    };

  private getShapeOptions(area: Area) {
    const exclusive = String(area.is_exclusive) === '1' || area.is_exclusive === true;
    return exclusive ? this._shapeOptions : this._nonExclusiveShapeOptions;
  }

    // Helpers functions
    private isGooglePolygon(shape) {
        return shape instanceof google.maps.Polygon
    }

    private isGoogleCircle(shape) {
        return shape instanceof google.maps.Circle
    }

    private isGoogleMarker(shape) {
        return shape instanceof google.maps.Marker
    }

    private isGooglePolyline(shape) {
        return shape instanceof google.maps.Polyline
    }

    private hasAreaCustomTypeAndIsCircle(area: Area) {
        return area.type == AreaType.custom && area.radius
    }

    private hasAreaCustomTypeAndIsPolygon(area: Area) {
        return area.type == AreaType.custom && area.geometry
    }

    private mapTupleLonLatToGoogleLatLngLiteral(lonLat: [number, number]) {
        return { lng: lonLat[0], lat: lonLat[1] }
    }

    private mapGoogleLatLngLiteralToTupleLonLat(latLng: { lat: () => number, lng: () => number }): [number, number] {
        return [latLng.lng(), latLng.lat()]
    }

    constructor(
        private route: ActivatedRoute,
        private areaService: AreaService
    ) { }

    ngAfterViewInit() {
        this.initMap()
        this.initDrawingManager()
        this.initInfoWindow()
        this.initAreas()
        this.initSearchBox()
    }

    private initMap() {
        this.map = new google.maps.Map(document.getElementById('map'), {
            center: { lat: 52, lng: 20 },
            zoom: 7
        })
        this.mapBounds = new google.maps.LatLngBounds()
        let _this = this;
        window['google'].maps.event.addListener(this.map, 'click', function () {
          if (_this.infoWindow) {
            _this.infoWindow.close();
          }
        });
    }

    private initDrawingManager() {
        this.drawingManager = new google.maps.drawing.DrawingManager({
            drawingControl: false,
            drawingControlOptions: {
                position: google.maps.ControlPosition.TOP_CENTER,
                drawingModes: ['polygon']
            },
            polygonOptions: this._shapeOptions
        })

        this.drawingManager.setMap(this.map)
    }

    private initInfoWindow() {
        this.infoWindow = new window['google'].maps.InfoWindow({ disableAutoPan: true })
    }

    private initAreas() {
        this.areas.forEach(a => {
            const shape = this.mapAreaToGoogleShape(a)
            if (Array.isArray(shape)) {
              shape.forEach((drawnShape) => {
                drawnShape.setMap(this.map);
                this.attachInfoWindow(drawnShape, a);
              });
            } else {
              shape.setMap(this.map);
              this.attachInfoWindow(shape, a);
            }
            this.addFacilityMarkersForArea(a);
        })
    }

    private initSearchBox() {
        const input = document.getElementById('searchBox')
        this.searchBox = new google.maps.places.SearchBox(input)
        this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(input)

        this.searchBox.addListener('places_changed', () => {
            const places = this.searchBox.getPlaces()

            if (!places.length)
                return

            this.clearSearchResultMarkers()

            // For each place, get the icon, name and location.
            var bounds = new google.maps.LatLngBounds()
            places.forEach(p => {
                if (!p.geometry) {
                    console.log("Returned place contains no geometry")
                    return
                }

                const icon = {
                    url: p.icon,
                    size: new google.maps.Size(71, 71),
                    origin: new google.maps.Point(0, 0),
                    anchor: new google.maps.Point(17, 34),
                    scaledSize: new google.maps.Size(25, 25)
                }

                // Create a marker for each place.
                this.searchResultMarkers.push(new google.maps.Marker({
                    map: this.map,
                    icon: icon,
                    title: p.name,
                    position: p.geometry.location
                }));

                if (p.geometry.viewport) {
                    // Only geocodes have viewport.
                    bounds.union(p.geometry.viewport)
                } else {
                    bounds.extend(p.geometry.location)
                }
            })
            this.map.fitBounds(bounds)
        })
    }

    private clearSearchResultMarkers() {
        this.searchResultMarkers.forEach(m => {
            m.setMap(null)
        })
        this.searchResultMarkers = []
    }

    private mapAreaToGoogleShape(area: Area) {

        if (area.type == AreaType.circle || this.hasAreaCustomTypeAndIsCircle(area)) {
            const { lat, lon, radius } = area
            return this.createGoogleCircle(lat, lon, radius, area)
        }
        else if (area.type == AreaType.administrative || this.hasAreaCustomTypeAndIsPolygon(area))
            return this.mapGeometryToGoogleShape(area.geometry, area)
    }

    private createGoogleCircle(lat: number, lng: number, radius: number, area: Area) {
        const center = { lat, lng }
        return new google.maps.Circle({ ...this.getShapeOptions(area), center, radius })
    }

    private mapGeometryToGoogleShape(geometry: Geometry, area: Area) {
        let shape;
        let paths;
        const shapeOptions = this.getShapeOptions(area);
        switch (geometry.type) {
            case 'Point':
                const position = this.mapTupleLonLatToGoogleLatLngLiteral(geometry.coordinates)
                return new google.maps.Marker({ ...shapeOptions, position })
            case 'LineString':
                const path = geometry.coordinates.map(lonLat => this.mapTupleLonLatToGoogleLatLngLiteral(lonLat))
                return new google.maps.Polyline({ ...shapeOptions, path })
            case 'Polygon':
              paths = [geometry.coordinates[0].map(lonLat => this.mapTupleLonLatToGoogleLatLngLiteral(lonLat))]
                return new google.maps.Polygon({ ...shapeOptions, paths })
            case 'MultiPolygon':
              let shapeArr = [];
              geometry.coordinates.forEach((polygonCoords, index) => {
                paths = [polygonCoords[0].map(lonLat => this.mapTupleLonLatToGoogleLatLngLiteral(lonLat))];
                shape = new window['google'].maps.Polygon({...shapeOptions, paths});
                shapeArr.push(shape);
              });
              return shapeArr;
            default:
                throw new Error('Shape not supported, allowed are: Point, LineString, Polygon.')
        }
    }

    private createAreaLabel(area: Area, areaLabelId: string) {
        const tags = area.tags
        const text = tags ? tags.map(t => t.short_name).join(', ') : ''

        const div = document.createElement('div')
        div.id = areaLabelId
        div.innerHTML = text

        return div
    }

    private attachInfoWindow(shape, area: Area) {
        if (this.isGoogleCircle(shape) && shape.getRadius() <= 1000)
            this.attachInfoWindowToSmallCircle(shape, area)
        else
            this.attachInfoWindowToShapeOnHover(shape, area)
    }

    private attachInfoWindowToShapeOnHover(shape, area: Area) {
        const latLng = this.getGoogleShapeCenter(shape)
        const label = this.createAreaLabel(area, this.areaLabelId)
        const header = this.createHeaderLabel(area, this.areaLabelId);
        label.innerHTML = `${header.outerHTML} <br/> ${label.innerHTML}`;

        window['google'].maps.event.addListener(shape, 'click', (e) => {
            this.infoWindow.setContent(label)
            this.infoWindow.setPosition(latLng)
            // this.infoWindow.setHeaderContent(header);
            this.infoWindow.setHeaderDisabled(true);
            this.infoWindow.open(this.map, shape)
        })
    }

    private attachInfoWindowToSmallCircle(shape, area: Area) {
        const infoWindow = new window['google'].maps.InfoWindow({ zIndex: 1, disableAutoPan: true })
        const areaLabelId = `areaLabel_${new Date().valueOf()}`

        const label = this.createAreaLabel(area, areaLabelId)
        const header = this.createHeaderLabel(area, this.areaLabelId);
        label.innerHTML = `${header.outerHTML} <br/> ${label.innerHTML}`;

        const latLng = this.getGoogleShapeCenter(shape)
        infoWindow.setContent(label)
        infoWindow.setPosition(latLng)
        // infoWindow.setHeaderContent(header);
        infoWindow.setHeaderDisabled(true);
        infoWindow.open(this.map)
    }

    createHeaderLabel(area: Area, areaLabelId: string) {
        const span = document.createElement('span')
        span.style.fontWeight = 'bold'

        // Use partner information if available, otherwise use area name
        if (area.partner && area.partner.full_name) {
            span.innerHTML = area.partner.full_name
        } else {
            span.innerHTML = area.name
        }

        return span
    }

    private createFacilityMarker(lat: number, lng: number, title?: string) {
        const position = { lat, lng };
        this.mapBounds.extend(position);

        return new google.maps.Marker({
            position,
            icon: this.yellowPinIconUrl,
            title: title || ''
        });
    }

    private getFacilityFullAddress(f): string {
        let parts = [];
        if (f && f.address) { parts.push(f.address); }

        let cityLine = '';
        if (f && f.postcode) { cityLine += f.postcode + ' '; }
        if (f && f.city) { cityLine += f.city; }
        if (cityLine) { parts.push(cityLine); }

        if (f && f.country) { parts.push(f.country); }

        return parts.join(', ');
    }

    private addFacilityMarkersForArea(area: Area) {
        if (!area || !area.facilities || area.facilities.length === 0) {
            return;
        }

        var _this = this;

        area.facilities.forEach(function (f) {
            if (f && typeof f.lat === 'number' && typeof f.lon === 'number') {
                var marker = _this.createFacilityMarker(f.lat, f.lon, f.name);
                marker.setMap(_this.map);

                google.maps.event.addListener(marker, 'click', function () {
                    var facilityIdLabel = 'ID';
                    var contentHtml = '' +
                        '<div class="facility-info-window">' +
                        '<div><strong>' + facilityIdLabel + ': ' + (f.id != null ? f.id : '') + '</strong></div>' +
                        '<div>' + (f.name ? f.name : '') + '</div>' +
                        '<div>' + _this.getFacilityFullAddress(f) + '</div>' +
                        '</div>';

                    _this.infoWindow.setContent(contentHtml);
                    _this.infoWindow.setPosition(marker.getPosition());
                    _this.infoWindow.setHeaderDisabled(true);
                    _this.infoWindow.open(_this.map, marker);
                });
            }
        });
    }


    // Returns type: google.maps.LatLngLiteral
    private getGoogleShapeCenter(shape): { lat: number, lng: number } {
        const bounds = this.getGoogleShapeBounds(shape)
        const center = bounds.getCenter()

        return { lat: center.lat(), lng: center.lng() }
    }

    // Returns type: google.maps.LatLngBounds
    private getGoogleShapeBounds(shape) {
        let bounds = new google.maps.LatLngBounds()

        if (this.isGooglePolygon(shape) || this.isGooglePolyline(shape)) {
            const path = shape.getPath().getArray()

            path.forEach(latLng => {
                bounds.extend({ lng: latLng.lng(), lat: latLng.lat() })
            })

            return bounds
        }
        else if (this.isGoogleCircle(shape)) {
            return shape.getBounds()
        }
        else if (this.isGoogleMarker(shape)) {
            const position = shape.getPosition()
            bounds.extend(position)

            return bounds
        }
    }
}



// WEBPACK FOOTER //
// /app/src/app/map/map.component.ts